import React, { useEffect, useState, createElement } from 'react';
import { useRituals, LootboxReward } from './RitualContext';
import { Trophy, Gift, Calendar, Clock, Diamond, Sparkles, Crown, ChevronRight, Package, Star, CheckCircle, BadgeCheck, Flame, Heart, Camera, MessageSquare, AlertTriangle, LockIcon, UnlockIcon, ArrowRight, Info, X, Plus, Shield } from 'lucide-react';
import { LootboxOpening } from './LootboxOpening';
import { LootboxDashboard } from './LootboxDashboard';
export const RitualRewards: React.FC = () => {
  const {
    streakCount,
    weeklyStreakCount,
    pendingLootbox,
    claimWeeklyLootbox,
    todayCompletedCount,
    totalDailyRituals,
    weeklyCompletedCount,
    totalWeeklyRituals,
    dailyRituals,
    weeklyRituals,
    funRituals,
    monetizationRituals,
    completeRitual,
    skipRitual,
    areAllRequiredRitualsCompleted
  } = useRituals();
  const [activeTab, setActiveTab] = useState<'daily' | 'weekly' | 'premium' | 'events'>('weekly');
  const [showLootbox, setShowLootbox] = useState(false);
  const [lootboxReward, setLootboxReward] = useState<LootboxReward | null>(null);
  const [showReward, setShowReward] = useState(false);
  const [showLootboxDashboard, setShowLootboxDashboard] = useState(false);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showRewardDetails, setShowRewardDetails] = useState<string | null>(null);
  // Check if all required rituals are completed
  const requiredRitualsCompleted = areAllRequiredRitualsCompleted();
  // Find premium ritual that's required but not completed
  const requiredPremiumRitual = weeklyRituals.find(ritual => ritual.requiredForLootbox && !ritual.completed);
  useEffect(() => {
    // Add custom animations to the stylesheet
    const style = document.createElement('style');
    style.textContent = `
      @keyframes float-particles {
        0% { transform: translateY(0) translateX(0); opacity: 1; }
        100% { transform: translateY(-20px) translateX(var(--random-x, 10px)); opacity: 0; }
      }
      @keyframes glow-pulse {
        0%, 100% { filter: drop-shadow(0 0 2px rgba(255, 255, 255, 0.5)); }
        50% { filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.8)); }
      }
      @keyframes treasure-bounce {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-5px); }
      }
      .animate-float-1 {
        --random-x: 10px;
        animation: float-particles 3s infinite ease-out;
        animation-delay: 0s;
      }
      .animate-float-2 {
        --random-x: -15px;
        animation: float-particles 2.5s infinite ease-out;
        animation-delay: 0.5s;
      }
      .animate-float-3 {
        --random-x: 5px;
        animation: float-particles 3.5s infinite ease-out;
        animation-delay: 1s;
      }
      .animate-float-4 {
        --random-x: -8px;
        animation: float-particles 3s infinite ease-out;
        animation-delay: 1.5s;
      }
      .animate-glow-pulse {
        animation: glow-pulse 2s infinite ease-in-out;
      }
      .animate-treasure-bounce {
        animation: treasure-bounce 2s infinite ease-in-out;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  // Handle tab changes from other components
  useEffect(() => {
    const handleSetRewardTab = (event: CustomEvent) => {
      if (event.detail && event.detail.tab) {
        setActiveTab(event.detail.tab);
      }
    };
    document.addEventListener('setRewardTab', handleSetRewardTab as EventListener);
    return () => {
      document.removeEventListener('setRewardTab', handleSetRewardTab as EventListener);
    };
  }, []);
  const handleOpenLootbox = async () => {
    if (!requiredRitualsCompleted) {
      setShowPremiumModal(true);
      return;
    }
    setShowLootbox(true);
    // Simulate delay for animation
    setTimeout(async () => {
      try {
        const reward = await claimWeeklyLootbox();
        setLootboxReward(reward);
        setShowReward(true);
      } catch (error) {
        setShowLootbox(false);
      }
    }, 1500);
  };
  const handleCloseLootboxReward = () => {
    setShowLootbox(false);
    setShowReward(false);
    setLootboxReward(null);
  };
  const closePremiumModal = () => {
    setShowPremiumModal(false);
  };
  const navigateToPremium = () => {
    // Navigate to premium section
    const event = new CustomEvent('navigate', {
      detail: {
        section: 'premium'
      }
    });
    document.dispatchEvent(event);
    setShowPremiumModal(false);
  };
  const navigateToRituals = () => {
    // Navigate to rituals section
    const event = new CustomEvent('navigate', {
      detail: {
        section: 'rituals'
      }
    });
    document.dispatchEvent(event);
  };
  const openLootboxDashboard = () => {
    setShowLootboxDashboard(true);
  };
  // Calculate non-premium rituals completion
  const nonPremiumWeeklyRituals = weeklyRituals.filter(r => !r.requiredForLootbox);
  const nonPremiumCompletedCount = nonPremiumWeeklyRituals.filter(r => r.completed).length;
  const totalNonPremiumWeeklyRituals = nonPremiumWeeklyRituals.length;
  // Get rarity styling
  const getRarityStyles = (rarity: string) => {
    switch (rarity) {
      case 'common':
        return {
          bg: 'from-gray-600 to-gray-700',
          border: 'border-gray-400',
          text: 'text-gray-300',
          gradientBg: 'from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800',
          lightBg: 'bg-gray-100 dark:bg-gray-800'
        };
      case 'rare':
        return {
          bg: 'from-blue-600 to-blue-700',
          border: 'border-blue-400',
          text: 'text-blue-300',
          gradientBg: 'from-blue-200 to-blue-300 dark:from-blue-900/40 dark:to-blue-800/40',
          lightBg: 'bg-blue-100 dark:bg-blue-900/30'
        };
      case 'epic':
        return {
          bg: 'from-purple-600 to-purple-700',
          border: 'border-purple-400',
          text: 'text-purple-300',
          gradientBg: 'from-purple-200 to-purple-300 dark:from-purple-900/40 dark:to-purple-800/40',
          lightBg: 'bg-purple-100 dark:bg-purple-900/30'
        };
      case 'legendary':
        return {
          bg: 'from-amber-500 to-amber-600',
          border: 'border-amber-400',
          text: 'text-amber-300',
          gradientBg: 'from-amber-200 to-amber-300 dark:from-amber-900/40 dark:to-amber-800/40',
          lightBg: 'bg-amber-100 dark:bg-amber-900/30'
        };
      default:
        return {
          bg: 'from-gray-600 to-gray-700',
          border: 'border-gray-400',
          text: 'text-gray-300',
          gradientBg: 'from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800',
          lightBg: 'bg-gray-100 dark:bg-gray-800'
        };
    }
  };
  // Render ritual item for daily/fun sections
  const renderRitualItem = (ritual: Ritual) => {
    const progressPercentage = ritual.progress / ritual.target * 100;
    const isPremiumRitual = ritual.requiredForLootbox || ritual.premiumOnly;
    return <div key={ritual.id} className={`p-3 ${ritual.completed ? 'bg-green-50 dark:bg-green-900/20' : isPremiumRitual ? 'bg-gradient-to-r from-amber-50/50 to-yellow-50/50 dark:from-amber-900/30 dark:to-yellow-900/30' : 'bg-white dark:bg-gray-900'} rounded-lg ${isPremiumRitual && !ritual.completed ? 'border-2 border-amber-300 dark:border-amber-700 shadow-[0_0_10px_rgba(251,191,36,0.3)] dark:shadow-[0_0_10px_rgba(251,191,36,0.2)]' : 'border border-gray-200 dark:border-gray-800'} shadow-sm hover:shadow-md transition-all duration-200 relative`} data-id="element-18191">
        {isPremiumRitual && !ritual.completed && <div className="absolute -top-2 -right-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1 animate-pulse-subtle" data-id="element-18192">
            <Flame size={12} data-id="element-18193" />
            <span data-id="element-18194">PREMIUM</span>
          </div>}
        <div className="flex items-start gap-3" data-id="element-18195">
          <div className={`w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center ${ritual.completed ? 'bg-green-100 dark:bg-green-800/30 text-green-600 dark:text-green-400' : isPremiumRitual ? 'bg-gradient-to-r from-amber-100 to-yellow-100 dark:from-amber-800/30 dark:to-yellow-800/30 text-amber-600 dark:text-amber-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`} data-id="element-18196">
            {ritual.icon === 'MessageSquare' && <MessageSquare size={20} data-id="element-18197" />}
            {ritual.icon === 'Heart' && <Heart size={20} data-id="element-18198" />}
            {ritual.icon === 'Camera' && <Camera size={20} data-id="element-18199" />}
            {ritual.icon === 'Diamond' && <Diamond size={20} data-id="element-18200" />}
            {ritual.icon === 'Zap' && <Flame size={20} data-id="element-18201" />}
            {ritual.icon === 'Gift' && <Gift size={20} data-id="element-18202" />}
            {ritual.icon === 'Trophy' && <Trophy size={20} data-id="element-18203" />}
            {ritual.icon === 'Package' && <Package size={20} data-id="element-18204" />}
            {ritual.icon === 'Sparkles' && <Sparkles size={20} data-id="element-18205" />}
          </div>
          <div className="flex-1" data-id="element-18206">
            <div className="flex items-center justify-between" data-id="element-18207">
              <h3 className="font-medium text-sm flex items-center gap-1.5" data-id="element-18208">
                {ritual.title}
                {ritual.isNew && <span className="bg-blue-500 text-white text-[10px] px-1.5 py-0.5 rounded-full" data-id="element-18209">
                    NEW
                  </span>}
                {ritual.isHot && !ritual.requiredForLootbox && <span className="bg-pink-500 text-white text-[10px] px-1.5 py-0.5 rounded-full" data-id="element-18210">
                    HOT
                  </span>}
              </h3>
              <div className="text-xs font-semibold" data-id="element-18211">
                {ritual.reward.type === 'tokens' && <span className="text-pink-500" data-id="element-18212">
                    {ritual.reward.amount} 💗
                  </span>}
                {ritual.reward.type === 'gems' && <span className="text-blue-500" data-id="element-18213">
                    {ritual.reward.amount} 💎
                  </span>}
                {ritual.reward.type === 'lootbox' && <span className="text-amber-500" data-id="element-18214">🎁 Lootbox</span>}
              </div>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1" data-id="element-18215">
              {ritual.description}
            </p>
            {ritual.requiredForLootbox && <div className="mt-1 flex items-center gap-1 text-amber-600 dark:text-amber-400 text-xs font-medium" data-id="element-18216">
                <LockIcon size={12} data-id="element-18217" />
                <span data-id="element-18218">Required for Weekly Lootbox</span>
              </div>}
            <div className="mt-2" data-id="element-18219">
              <div className="flex items-center justify-between mb-1" data-id="element-18220">
                <span className="text-xs text-gray-500 dark:text-gray-500" data-id="element-18221">
                  Progress
                </span>
                <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-18222">
                  {ritual.progress}/{ritual.target}
                </span>
              </div>
              <div className="h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden" data-id="element-18223">
                <div className={`h-full rounded-full transition-all duration-500 ${ritual.completed ? 'bg-green-500' : ritual.requiredForLootbox ? 'bg-gradient-to-r from-amber-500 to-yellow-500' : ritual.type === 'daily' ? 'bg-gradient-to-r from-pink-500 to-purple-500' : ritual.type === 'weekly' ? 'bg-gradient-to-r from-blue-500 to-purple-500' : ritual.type === 'monetization' ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-gradient-to-r from-cyan-500 to-blue-500'}`} style={{
                width: `${progressPercentage}%`
              }} data-id="element-18224"></div>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2" data-id="element-18225">
              {ritual.completed ? <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 font-medium" data-id="element-18226">
                  <CheckCircle size={14} data-id="element-18227" />
                  <span data-id="element-18228">Completed</span>
                </div> : <>
                  {ritual.premiumOnly || ritual.requiredForLootbox ? <button className="flex-1 py-1.5 rounded-full text-xs font-medium transition-all duration-300 flex items-center justify-center gap-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-md hover:shadow-amber-500/20 relative overflow-hidden" onClick={() => {
                // For premium rituals, navigate to premium section
                const event = new CustomEvent('navigate', {
                  detail: {
                    section: 'premium'
                  }
                });
                document.dispatchEvent(event);
              }} onMouseEnter={() => setShowTooltip(ritual.id)} onMouseLeave={() => setShowTooltip(null)} data-id="element-18229">
                      <span data-id="element-18230">Unlock Now</span>
                      <Diamond size={14} data-id="element-18231" />
                      <div className="absolute inset-0 bg-white/20 animate-shimmer" style={{
                  transform: 'translateX(-100%)'
                }} data-id="element-18232"></div>
                      {showTooltip === ritual.id && <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-gray-900 text-white text-xs rounded py-1 px-2 shadow-lg z-10" data-id="element-18233">
                          {ritual.requiredForLootbox ? 'This ritual is required to claim the Weekly Lootbox' : 'Unlock premium ritual for exclusive rewards'}
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900" data-id="element-18234"></div>
                        </div>}
                    </button> : <button className={`flex-1 py-1.5 rounded-full text-xs font-medium transition-all duration-300 flex items-center justify-center gap-1.5 ${ritual.type === 'daily' ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white hover:shadow-md hover:shadow-pink-500/20' : ritual.type === 'weekly' ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-md hover:shadow-blue-500/20' : ritual.type === 'monetization' ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-md hover:shadow-amber-500/20' : 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white hover:shadow-md hover:shadow-cyan-500/20'}`} onClick={() => {
                // For demo purposes, clicking "Go" will complete the ritual
                completeRitual(ritual.id);
              }} data-id="element-18235">
                      <span data-id="element-18236">Go</span>
                      <ChevronRight size={14} data-id="element-18237" />
                    </button>}
                  {ritual.type !== 'monetization' && !ritual.requiredForLootbox && <button className="py-1.5 px-2 bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium hover:bg-gray-300 dark:hover:bg-gray-700 transition-all duration-300 flex items-center justify-center" onClick={() => skipRitual(ritual.id)} data-id="element-18238">
                        <Clock size={14} data-id="element-18239" />
                      </button>}
                </>}
            </div>
          </div>
        </div>
      </div>;
  };
  return <div className="w-full max-w-4xl mx-auto" data-id="element-18240">
      {/* Header with streak info */}
      <div className="bg-gradient-to-r from-pink-600 to-purple-600 rounded-lg p-4 text-white shadow-lg mb-6" data-id="element-18241">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4" data-id="element-18242">
          <div data-id="element-18243">
            <h2 className="text-xl font-bold mb-1" data-id="element-18244">Ritual Rewards</h2>
            <p className="text-white/80 text-sm" data-id="element-18245">
              Complete rituals to earn tokens, gems, and exclusive content
            </p>
          </div>
          <div className="flex items-center gap-4" data-id="element-18246">
            <div className="flex flex-col items-center" data-id="element-18247">
              <div className="flex items-center gap-1.5 mb-1" data-id="element-18248">
                <Flame size={16} className="text-amber-300" data-id="element-18249" />
                <span className="text-sm font-medium" data-id="element-18250">Daily Streak</span>
              </div>
              <div className="bg-white/20 rounded-full px-3 py-1 text-sm font-bold" data-id="element-18251">
                {streakCount} Days 🔥
              </div>
            </div>
            <div className="flex flex-col items-center" data-id="element-18252">
              <div className="flex items-center gap-1.5 mb-1" data-id="element-18253">
                <Calendar size={16} data-id="element-18254" />
                <span className="text-sm font-medium" data-id="element-18255">Weekly Streak</span>
              </div>
              <div className="bg-white/20 rounded-full px-3 py-1 text-sm font-bold" data-id="element-18256">
                {weeklyStreakCount} Weeks ✨
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Weekly Lootbox Status Banner - Always shown at top */}
      <div className={`mb-6 p-4 rounded-lg ${requiredRitualsCompleted ? 'bg-gradient-to-r from-amber-500 to-yellow-500' : 'bg-gradient-to-r from-gray-500 to-gray-600'} text-white relative overflow-hidden`} data-id="element-18257">
        <div className="flex items-center gap-4" data-id="element-18258">
          <div className={`relative ${requiredRitualsCompleted ? '' : 'grayscale opacity-80'}`} data-id="element-18259">
            <div className={`w-16 h-16 ${requiredRitualsCompleted ? 'bg-gradient-to-b from-yellow-400 to-amber-600 animate-treasure-bounce' : 'bg-white/10'} rounded-lg flex items-center justify-center ${requiredRitualsCompleted ? 'animate-glow-pulse' : ''}`} data-id="element-18260">
              <Gift size={32} className="text-white" data-id="element-18261" />
            </div>
            {!requiredRitualsCompleted && <div className="absolute inset-0 flex items-center justify-center" data-id="element-18262">
                <LockIcon size={24} className="text-white/90" data-id="element-18263" />
              </div>}
          </div>
          <div className="flex-1" data-id="element-18264">
            <h3 className="font-bold text-lg mb-1" data-id="element-18265">Weekly Lootbox Status</h3>
            <p className="text-white/90 text-sm" data-id="element-18266">
              {requiredRitualsCompleted ? 'All rituals completed! Your epic lootbox is ready to open.' : "Complete your Premium Ritual to unlock this week's epic lootbox!"}
            </p>
            <div className="mt-2 flex items-center gap-2" data-id="element-18267">
              <div className="flex-1 h-2 bg-white/20 rounded-full overflow-hidden" data-id="element-18268">
                <div className="h-full bg-white rounded-full" style={{
                width: `${nonPremiumCompletedCount / totalNonPremiumWeeklyRituals * 100}%`
              }} data-id="element-18269"></div>
              </div>
              <span className="text-xs font-medium" data-id="element-18270">
                {nonPremiumCompletedCount}/{totalNonPremiumWeeklyRituals}{' '}
                Regular
              </span>
            </div>
            <div className="mt-1 flex items-center gap-2" data-id="element-18271">
              <div className="flex-1 h-2 bg-white/20 rounded-full overflow-hidden" data-id="element-18272">
                <div className={`h-full rounded-full ${requiredRitualsCompleted ? 'bg-white' : 'bg-amber-300/50'}`} style={{
                width: requiredRitualsCompleted ? '100%' : '0%'
              }} data-id="element-18273"></div>
              </div>
              <span className="text-xs font-medium flex items-center gap-1" data-id="element-18274">
                {requiredRitualsCompleted ? <CheckCircle size={12} data-id="element-18275" /> : <LockIcon size={12} data-id="element-18276" />}
                Premium
              </span>
            </div>
          </div>
          {!requiredRitualsCompleted ? <button className="px-4 py-2 bg-gradient-to-r from-amber-400 to-yellow-400 text-amber-900 rounded-full text-sm font-medium hover:shadow-lg hover:shadow-amber-500/20 transition-all flex items-center gap-1.5" onClick={() => setActiveTab('premium')} data-id="element-18277">
              <LockIcon size={14} data-id="element-18278" />
              <span data-id="element-18279">Unlock Now</span>
            </button> : <button className="px-4 py-2 bg-gradient-to-r from-yellow-300 to-amber-500 text-amber-900 rounded-full text-sm font-medium hover:shadow-lg hover:shadow-amber-500/20 transition-all duration-300 animate-pulse-subtle flex items-center gap-1.5" onClick={handleOpenLootbox} data-id="element-18280">
              <Sparkles size={14} data-id="element-18281" />
              <span data-id="element-18282">Open Lootbox!</span>
            </button>}
        </div>
        {/* Animated particles */}
        {requiredRitualsCompleted && <>
            <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white rounded-full animate-float-1" data-id="element-18283"></div>
            <div className="absolute top-1/3 right-1/4 w-3 h-3 bg-white/70 rounded-full animate-float-2" data-id="element-18284"></div>
            <div className="absolute bottom-1/4 left-1/3 w-2 h-2 bg-white/60 rounded-full animate-float-3" data-id="element-18285"></div>
          </>}
      </div>
      {/* Lootbox Preview Section */}
      <div className="mb-6" data-id="element-18286">
        <div className="flex items-center justify-between mb-4" data-id="element-18287">
          <h3 className="text-lg font-bold dark:text-white flex items-center gap-2" data-id="element-18288">
            <Gift size={20} className="text-amber-500" data-id="element-18289" />
            <span data-id="element-18290">Weekly Lootbox Rewards</span>
          </h3>
          <button className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg text-sm font-medium hover:shadow-lg hover:shadow-purple-500/20 transition-all flex items-center gap-1.5" onClick={openLootboxDashboard} data-id="element-18291">
            <Package size={16} data-id="element-18292" />
            <span data-id="element-18293">View All Lootboxes</span>
          </button>
        </div>
        {/* Possible rewards cards with rarity */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3" data-id="element-18294">
          {[{
          id: 'reward-1',
          title: 'Gem Bundle',
          description: '100 premium Gems to spend in the marketplace',
          rarity: 'epic',
          value: '💎 100',
          icon: <Diamond size={20} className="text-purple-300" data-id="element-18295" />
        }, {
          id: 'reward-2',
          title: 'VIP Week',
          description: '7 days of VIP membership with premium benefits',
          rarity: 'epic',
          value: '💗 500',
          icon: <Crown size={20} className="text-purple-300" data-id="element-18296" />
        }, {
          id: 'reward-3',
          title: 'Token Jackpot',
          description: 'A bundle of 250 LuvTokens to spend however you want',
          rarity: 'rare',
          value: '💗 250',
          icon: <Gift size={20} className="text-blue-300" data-id="element-18297" />
        }, {
          id: 'reward-4',
          title: 'Exclusive Angel Skin',
          description: 'Limited edition Angel skin not available elsewhere',
          rarity: 'legendary',
          value: '💎 200',
          icon: <Sparkles size={20} className="text-amber-300" data-id="element-18298" />
        }].map(reward => {
          const rarityStyle = getRarityStyles(reward.rarity);
          return <div key={reward.id} className={`relative overflow-hidden rounded-lg border ${rarityStyle.border} bg-white dark:bg-gray-900 hover:shadow-md transition-all duration-200 cursor-pointer group`} onClick={() => setShowRewardDetails(reward.id === showRewardDetails ? null : reward.id)} onMouseEnter={() => setShowTooltip(reward.id)} onMouseLeave={() => setShowTooltip(null)} data-id="element-18299">
                <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${rarityStyle.bg}`} data-id="element-18300"></div>
                <div className="p-3" data-id="element-18301">
                  <div className="flex items-start gap-3" data-id="element-18302">
                    <div className={`relative w-12 h-12 flex-shrink-0 flex items-center justify-center bg-gradient-to-br ${rarityStyle.bg} rounded-lg overflow-hidden`} data-id="element-18303">
                      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-30" data-id="element-18304"></div>
                      {reward.icon}
                    </div>
                    <div className="flex-1" data-id="element-18305">
                      <div className="flex items-start justify-between" data-id="element-18306">
                        <h4 className="font-medium text-sm dark:text-white" data-id="element-18307">
                          {reward.title}
                        </h4>
                        <div className={`px-1.5 py-0.5 rounded-sm text-xs bg-gradient-to-r ${rarityStyle.bg} ${rarityStyle.text}`} data-id="element-18308">
                          {reward.rarity.toUpperCase()}
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1" data-id="element-18309">
                        {reward.description}
                      </p>
                      <div className="mt-2 text-sm font-bold text-amber-600 dark:text-amber-400" data-id="element-18310">
                        Value: {reward.value}
                      </div>
                    </div>
                  </div>
                </div>
                {showTooltip === reward.id && <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-gray-900 text-white text-xs rounded py-1 px-2 shadow-lg z-10" data-id="element-18311">
                    {requiredRitualsCompleted ? 'Ready to claim! Open your weekly lootbox now.' : 'Complete all weekly rituals including the premium ritual to unlock.'}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900" data-id="element-18312"></div>
                  </div>}
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 dark:from-white/0 dark:via-white/5 dark:to-white/0 opacity-0 group-hover:opacity-100 transform -translate-x-full group-hover:translate-x-full transition-all duration-1000 ease-in-out" data-id="element-18313"></div>
              </div>;
        })}
        </div>
        {/* Premium required callout */}
        {!requiredRitualsCompleted && <div className="mt-4 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 rounded-lg border border-amber-200 dark:border-amber-800/30 shadow-sm" data-id="element-18314">
            <div className="flex items-center gap-3" data-id="element-18315">
              <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-gradient-to-r from-amber-500 to-orange-500 rounded-full text-white" data-id="element-18316">
                <AlertTriangle size={24} data-id="element-18317" />
              </div>
              <div className="flex-1" data-id="element-18318">
                <h4 className="font-medium text-amber-800 dark:text-amber-400" data-id="element-18319">
                  Premium Ritual Required
                </h4>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1" data-id="element-18320">
                  You must complete a premium ritual to unlock this week's
                  lootbox with exclusive rewards.
                </p>
                <button className="mt-2 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg text-xs font-medium hover:shadow-md hover:shadow-amber-500/20 transition-all duration-300 flex items-center gap-1.5 animate-pulse-subtle" onClick={() => setActiveTab('premium')} data-id="element-18321">
                  <Diamond size={14} data-id="element-18322" />
                  <span data-id="element-18323">View Premium Rituals</span>
                </button>
              </div>
            </div>
          </div>}
      </div>
      {/* Tabs for different ritual types */}
      <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide" data-id="element-18324">
        <button className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-1.5 ${activeTab === 'daily' ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white shadow-md' : 'bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800'}`} onClick={() => setActiveTab('daily')} data-id="element-18325">
          <Trophy size={16} data-id="element-18326" />
          <span data-id="element-18327">Daily Core Rituals</span>
        </button>
        <button className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-1.5 ${activeTab === 'weekly' ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md' : 'bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800'}`} onClick={() => setActiveTab('weekly')} data-id="element-18328">
          <Calendar size={16} data-id="element-18329" />
          <span data-id="element-18330">Weekly Rituals</span>
          {!requiredRitualsCompleted && <span className="ml-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" data-id="element-18331"></span>}
        </button>
        <button className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-1.5 ${activeTab === 'premium' ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md' : 'bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800'} ${!requiredRitualsCompleted ? 'animate-pulse-subtle relative overflow-hidden' : ''}`} onClick={() => setActiveTab('premium')} data-id="element-18332">
          <Diamond size={16} data-id="element-18333" />
          <span data-id="element-18334">Premium Rituals</span>
          {!requiredRitualsCompleted && <span className="ml-1 bg-amber-200 text-amber-900 text-[10px] px-1.5 py-0.5 rounded-full animate-pulse-subtle" data-id="element-18335">
              Required
            </span>}
          {!requiredRitualsCompleted && <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 opacity-0 group-hover:opacity-100 transform -translate-x-full animate-shimmer" data-id="element-18336"></div>}
        </button>
        <button className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-1.5 ${activeTab === 'events' ? 'bg-gradient-to-r from-green-600 to-teal-600 text-white shadow-md' : 'bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800'}`} onClick={() => setActiveTab('events')} data-id="element-18337">
          <Star size={16} data-id="element-18338" />
          <span data-id="element-18339">Special Events</span>
        </button>
      </div>
      {/* Premium Rituals Explainer (shown when premium tab is active) */}
      {activeTab === 'premium' && <div className="mb-6 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border border-amber-200 dark:border-amber-800/30 rounded-lg p-4 shadow-sm" data-id="element-18340">
          <div className="flex flex-col md:flex-row items-start gap-4" data-id="element-18341">
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-amber-400 to-orange-400 flex items-center justify-center flex-shrink-0" data-id="element-18342">
              <Diamond size={32} className="text-white" data-id="element-18343" />
            </div>
            <div data-id="element-18344">
              <h3 className="text-lg font-bold text-amber-800 dark:text-amber-400 mb-1" data-id="element-18345">
                Premium Rituals Unlock Weekly Lootbox
              </h3>
              <p className="text-sm text-amber-700 dark:text-amber-300 mb-3" data-id="element-18346">
                Only Premium Supporters can unlock the full reward. Complete
                premium rituals to earn exclusive rewards and access your Weekly
                Lootbox.
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-18347">
                <div className="bg-amber-200 dark:bg-amber-800/50 text-amber-800 dark:text-amber-300 text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1" data-id="element-18348">
                  <CheckCircle size={12} data-id="element-18349" />
                  <span data-id="element-18350">Double XP Rewards</span>
                </div>
                <div className="bg-amber-200 dark:bg-amber-800/50 text-amber-800 dark:text-amber-300 text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1" data-id="element-18351">
                  <CheckCircle size={12} data-id="element-18352" />
                  <span data-id="element-18353">Bonus Gems</span>
                </div>
                <div className="bg-amber-200 dark:bg-amber-800/50 text-amber-800 dark:text-amber-300 text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1" data-id="element-18354">
                  <CheckCircle size={12} data-id="element-18355" />
                  <span data-id="element-18356">Exclusive Skin Chances</span>
                </div>
                <div className="bg-amber-200 dark:bg-amber-800/50 text-amber-800 dark:text-amber-300 text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1" data-id="element-18357">
                  <CheckCircle size={12} data-id="element-18358" />
                  <span data-id="element-18359">Unlocks Weekly Lootbox</span>
                </div>
              </div>
            </div>
          </div>
        </div>}
      {/* Required Premium Ritual Highlight - shown in premium tab */}
      {activeTab === 'premium' && requiredPremiumRitual && <div className="mb-6 relative overflow-hidden rounded-xl border-2 border-amber-400 dark:border-amber-600 shadow-[0_0_15px_rgba(251,191,36,0.4)] dark:shadow-[0_0_15px_rgba(251,191,36,0.3)]" data-id="element-18360">
          <div className="absolute top-0 right-0 bg-gradient-to-bl from-amber-500 to-yellow-500 text-white px-3 py-1 text-xs font-bold" data-id="element-18361">
            REQUIRED FOR LOOTBOX
          </div>
          <div className="p-4 pt-8 bg-gradient-to-r from-amber-50/80 to-yellow-50/80 dark:from-amber-900/40 dark:to-yellow-900/40" data-id="element-18362">
            <div className="flex items-start gap-4" data-id="element-18363">
              <div className="w-14 h-14 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0 animate-pulse-subtle" data-id="element-18364">
                <LockIcon size={28} className="text-white" data-id="element-18365" />
              </div>
              <div className="flex-1" data-id="element-18366">
                <h3 className="text-lg font-bold text-amber-800 dark:text-amber-400 mb-1" data-id="element-18367">
                  {requiredPremiumRitual.title}
                </h3>
                <p className="text-sm text-amber-700 dark:text-amber-300 mb-3" data-id="element-18368">
                  {requiredPremiumRitual.description}
                </p>
                <div className="mb-3" data-id="element-18369">
                  <div className="flex items-center justify-between mb-1" data-id="element-18370">
                    <span className="text-xs text-amber-700 dark:text-amber-400" data-id="element-18371">
                      Progress
                    </span>
                    <span className="text-xs font-medium text-amber-800 dark:text-amber-300" data-id="element-18372">
                      {requiredPremiumRitual.progress}/
                      {requiredPremiumRitual.target}
                    </span>
                  </div>
                  <div className="h-2 bg-amber-200/50 dark:bg-amber-800/30 rounded-full overflow-hidden" data-id="element-18373">
                    <div className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full" style={{
                  width: `${requiredPremiumRitual.progress / requiredPremiumRitual.target * 100}%`
                }} data-id="element-18374"></div>
                  </div>
                </div>
                <button className="w-full py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full text-sm font-medium hover:shadow-lg hover:shadow-amber-500/20 transition-all duration-300 flex items-center justify-center gap-2 animate-pulse-subtle" onClick={() => {
              // Navigate to premium section
              const event = new CustomEvent('navigate', {
                detail: {
                  section: 'premium'
                }
              });
              document.dispatchEvent(event);
            }} data-id="element-18375">
                  <Diamond size={16} data-id="element-18376" />
                  <span data-id="element-18377">Unlock Premium Ritual Now</span>
                  <ArrowRight size={16} data-id="element-18378" />
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Ritual list */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-18379">
        {activeTab === 'daily' && dailyRituals.map(ritual => renderRitualItem(ritual))}
        {activeTab === 'weekly' && weeklyRituals.map(ritual => renderRitualItem(ritual))}
        {activeTab === 'premium' && [...weeklyRituals.filter(r => r.requiredForLootbox), ...monetizationRituals].map(ritual => renderRitualItem(ritual))}
        {activeTab === 'events' && <div className="md:col-span-2 text-center py-8" data-id="element-18380">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-gradient-to-r from-green-500 to-teal-500 rounded-full" data-id="element-18381">
              <Star size={24} className="text-white" data-id="element-18382" />
            </div>
            <h4 className="font-medium text-lg dark:text-white mb-1" data-id="element-18383">
              Summer Heat Event Coming Soon!
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 max-w-md mx-auto" data-id="element-18384">
              Our next special event launches in 14 days. Get ready for
              exclusive beach-themed rewards and challenges!
            </p>
            <button className="px-4 py-2 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-full text-sm font-medium hover:shadow-lg hover:shadow-green-500/20 transition-all duration-300" data-id="element-18385">
              Get Notified
            </button>
          </div>}
      </div>
      {/* Premium required modal */}
      {showPremiumModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-18386">
          <div className="relative max-w-md w-full rounded-2xl overflow-hidden bg-gradient-to-b from-amber-50 to-orange-50 dark:from-gray-900 dark:to-gray-950 p-6 shadow-2xl border border-amber-200 dark:border-amber-800/30 animate-in zoom-in-95 duration-300" data-id="element-18387">
            <button className="absolute right-4 top-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-gray-200/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-400" onClick={closePremiumModal} data-id="element-18388">
              <X size={18} data-id="element-18389" />
            </button>
            <div className="flex flex-col items-center text-center" data-id="element-18390">
              <div className="w-20 h-20 rounded-full bg-gradient-to-r from-amber-400 to-orange-400 flex items-center justify-center mb-4" data-id="element-18391">
                <LockIcon size={36} className="text-white" data-id="element-18392" />
              </div>
              <h2 className="text-xl font-bold text-amber-800 dark:text-amber-400 mb-2" data-id="element-18393">
                Premium Ritual Required
              </h2>
              <p className="text-amber-700 dark:text-amber-300 mb-4" data-id="element-18394">
                Complete the Premium Ritual to unlock your Weekly Epic Lootbox
                with exclusive rewards and mystery bonuses!
              </p>
              <div className="w-full bg-amber-200/50 dark:bg-amber-900/30 rounded-lg p-4 mb-4 text-left" data-id="element-18395">
                <h3 className="font-medium text-amber-800 dark:text-amber-400 mb-2 flex items-center gap-2" data-id="element-18396">
                  <AlertTriangle size={18} data-id="element-18397" />
                  <span data-id="element-18398">Missing Premium Ritual</span>
                </h3>
                <ul className="space-y-2" data-id="element-18399">
                  {weeklyRituals.filter(r => r.requiredForLootbox && !r.completed).map(ritual => <li key={ritual.id} className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300" data-id="element-18400">
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-500" data-id="element-18401"></div>
                        <span data-id="element-18402">{ritual.title}</span>
                      </li>)}
                </ul>
              </div>
              <button className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg text-sm font-medium hover:shadow-lg hover:shadow-amber-500/20 transition-all duration-300 flex items-center justify-center gap-2" onClick={navigateToPremium} data-id="element-18403">
                <Diamond size={18} data-id="element-18404" />
                <span data-id="element-18405">Unlock Premium Ritual Now</span>
                <ArrowRight size={18} data-id="element-18406" />
              </button>
              <button className="mt-3 text-xs text-amber-700 dark:text-amber-400 hover:underline" onClick={closePremiumModal} data-id="element-18407">
                I'll do this later
              </button>
            </div>
          </div>
        </div>}
      {/* Lootbox opening modal */}
      {showLootbox && <LootboxOpening reward={lootboxReward} showReward={showReward} onClose={handleCloseLootboxReward} data-id="element-18408" />}
      {/* View All Rituals button at bottom */}
      <div className="mt-6 flex justify-center" data-id="element-18409">
        <button className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full text-sm font-medium hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300 flex items-center gap-2" onClick={navigateToRituals} data-id="element-18410">
          <Trophy size={18} data-id="element-18411" />
          <span data-id="element-18412">View All Rituals</span>
          <ChevronRight size={18} data-id="element-18413" />
        </button>
      </div>
      {/* Lootbox Dashboard Modal */}
      {showLootboxDashboard && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-18414">
          <div className="relative max-w-4xl w-full max-h-[90vh] overflow-auto rounded-xl animate-in zoom-in-95 duration-300" data-id="element-18415">
            <LootboxDashboard userTier="silver" onUpgrade={navigateToPremium} onClose={() => setShowLootboxDashboard(false)} data-id="element-18416" />
          </div>
        </div>}
    </div>;
};